/**
 * The card stack that reads under (and above) the map.
 *
 * The sky answers "where am I"; these answer "what is this".  Every
 * string they print comes from the payload — `sentence`, `gaps`, the
 * claim's own words — because the display law makes those the only
 * learner-facing strings the truth plane produces, and inventing prose
 * here would put a second, un-versioned voice on the same reading.
 */

import { useState } from "react";

import { MONO, SERIF, type Palette, seasonColour, trackColour } from "./palette";
import { hash32 } from "./planetLook";
import type {
  AtlasPayload, ClaimReading, InstrumentReading, PlanetReading, RingReading, SectorReading,
  TrackReading,
} from "./types";
import { usePlanetIdea } from "./useAtlas";

const CARD = (P: Palette, accent?: string): React.CSSProperties => ({
  borderRadius: 12,
  border: `1px solid ${accent ? `${accent}77` : P.line}`,
  background: P.panel,
  marginTop: 10,
  overflow: "hidden",
});

const KICKER = (colour: string): React.CSSProperties => ({
  fontFamily: MONO,
  fontSize: 10,
  letterSpacing: "0.16em",
  color: colour,
});

/** Instrument kinds wear the reference's glyphs; the words are ours. */
const GLYPH: Record<string, string> = {
  stake: "◆",
  rep: "■",
  scenario: "▲",
  capstone: "★",
};

const KIND_WORD: Record<string, string> = {
  stake: "stake · short form",
  rep: "rep · application",
  scenario: "scenario · transfer",
  capstone: "capstone · the whole thing",
};

const STATE_WORD: Record<string, string> = {
  COLD: "cold",
  STIRRING: "at the bench",
  CLIMBING: "climbing",
  CROSSED: "crossed",
  GRANTED: "standing, not yet proven",
};

/**
 * The six colours an authored figure references (`var(--zai-line, #C6B78F)`
 * and so on).  The taxonomy ships every figure with the light hex as its
 * fallback, so a figure renders with no stylesheet at all; setting the
 * properties here is what makes the same markup follow the night sky.
 * The values are the atlas palette's own — the figures were drawn in it.
 */
const figurePalette = (P: Palette): React.CSSProperties =>
  ({
    "--zai-line": P.line,
    "--zai-text": P.text,
    "--zai-dim": P.dim,
    "--zai-faint": P.faint,
    "--zai-gold": P.gold,
    "--zai-russet": P.russet,
  }) as React.CSSProperties;

export function PlanetCard({
  planet, P, names, tracks, sectors, selectedClaim, onOpenRing, onPlanet, onClaim,
}: {
  planet: PlanetReading;
  P: Palette;
  /** id → title, so the card names worlds instead of printing registry ids. */
  names: Map<string, string>;
  tracks: TrackReading[];
  sectors?: SectorReading[];
  selectedClaim?: number | null;
  onOpenRing?: () => void;
  onPlanet?: (id: string) => void;
  onClaim?: (index: number) => void;
}) {
  const name = (id: string) => names.get(id) || id;
  const trackLabel = (id: string) => tracks.find((entry) => entry.id === id)?.label || id;
  const sectorLabel = sectors?.find((entry) => entry.id === planet.sector)?.label;
  const accent = planet.state === "COLD" ? P.line : seasonColour(P, planet.heat);
  // The card's figure and prose are fetched the first time it is opened
  // — one small request for the one world in view — and kept for the visit.
  const [ideaOpen, setIdeaOpen] = useState(false);
  const idea = usePlanetIdea(ideaOpen ? planet.id : null);
  return (
    <div style={CARD(P, accent)} data-testid="atlas-planet-card">
      <div style={{ padding: "10px 14px 12px" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ ...KICKER(accent), fontWeight: 700 }}>
            {STATE_WORD[planet.state].toUpperCase()}
          </span>
          {planet.ringName && (
            <button
              type="button" onClick={onOpenRing}
              style={{
                ...KICKER(P.faint), background: "none", border: "none", padding: 0,
                cursor: onOpenRing ? "pointer" : "default",
              }}
            >
              {planet.ringName.toUpperCase()}
            </button>
          )}
          {sectorLabel && (
            <span style={KICKER(P.faint)} data-testid="atlas-planet-sector">
              {sectorLabel.toUpperCase()}
            </span>
          )}
          <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 11, color: P.faint }}>
            {planet.claimsTotal} claim{planet.claimsTotal === 1 ? "" : "s"}
          </span>
        </div>
        <div style={{ fontFamily: SERIF, fontSize: 20, color: P.text, marginTop: 4 }}>
          {planet.title || planet.id}
        </div>
        {planet.hook && (
          <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, color: P.gold, lineHeight: 1.45, marginTop: 4 }}>
            {planet.hook}
          </div>
        )}
        {/* THE IDEA, COMPRESSED — the author's card: a headline, the
            field's figure where it drew one, and the prose.  Curriculum,
            not a reading, so it sits with the hook and folds shut: the
            learner's own sentence stays above the fold on a phone, and
            the card opens on a tap.  The figure is the registry's own
            markup — the taxonomy door holds it to one script-free
            <svg> — and its colours follow the sky (see figurePalette). */}
        {planet.picture && (
          <details
            style={{ marginTop: 8 }} data-testid="atlas-planet-idea"
            onToggle={(event) => setIdeaOpen((event.currentTarget as HTMLDetailsElement).open)}
          >
            <summary style={{ cursor: "pointer", fontFamily: SERIF, fontSize: 14, color: P.text, lineHeight: 1.45 }}>
              <span style={{ ...KICKER(P.faint), marginRight: 8 }}>THE IDEA, COMPRESSED</span>
              {planet.picture.title}
            </summary>
            {idea.status === "loading" && (
              <div style={{ fontFamily: MONO, fontSize: 11, color: P.faint, marginTop: 8 }}>drawing…</div>
            )}
            {idea.status === "error" && (
              <div style={{ fontFamily: MONO, fontSize: 11, color: P.russet, marginTop: 8 }}>{idea.error}</div>
            )}
            {idea.status === "ready" && idea.idea.svg && (
              <div
                role="img"
                aria-label={planet.picture.title}
                data-testid="atlas-planet-figure"
                style={{ ...figurePalette(P), maxWidth: 360, marginTop: 8 }}
                dangerouslySetInnerHTML={{ __html: idea.idea.svg }}
              />
            )}
            {idea.status === "ready" && idea.idea.description && (
              <p
                data-testid="atlas-planet-description"
                style={{ fontFamily: SERIF, fontSize: 14, color: P.dim, lineHeight: 1.55, margin: "8px 0 0" }}
              >
                {idea.idea.description}
              </p>
            )}
          </details>
        )}
        <div style={{ fontFamily: SERIF, fontSize: 14, color: P.dim, lineHeight: 1.5, marginTop: 6 }}>
          {planet.sentence}
        </div>
        {planet.gaps.length > 0 && (
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: P.dim, fontFamily: SERIF, fontSize: 13, lineHeight: 1.6 }}>
            {planet.gaps.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        )}
        <Ladder planet={planet} P={P} />
        {planet.nextClaim && (
          <div style={{ fontFamily: MONO, fontSize: 11.5, color: P.goldHi, marginTop: 8 }} data-testid="atlas-next-hint">
            start here · {planet.claims.find((claim) => claim.id === planet.nextClaim)?.title || planet.nextClaim}
          </div>
        )}
        {/* THE CLAIMS, AS A LIST.
            The map draws them as nodes on a trail, which is the picture
            — but a picture is a poor door: on a phone the nodes are a
            few millimetres apart, and to a screen reader or a keyboard
            an SVG group is barely there at all.  This is the same
            roster, reachable by tab and readable aloud, and it is how
            the shelf is opened without touching the drawing. */}
        {planet.claims.length > 0 && (
          <div style={{ marginTop: 10 }} data-testid="atlas-claim-list">
            {planet.claims.map((claim, index) => (
              <button
                key={claim.id}
                type="button"
                onClick={() => onClaim?.(index)}
                data-testid={`atlas-claim-row-${claim.id}`}
                style={{
                  width: "100%", textAlign: "left", display: "flex", gap: 10,
                  alignItems: "baseline", minHeight: 40, padding: "7px 8px",
                  borderRadius: 8, cursor: onClaim ? "pointer" : "default",
                  border: `1px solid ${selectedClaim === index ? P.goldHi : "transparent"}`,
                  background: selectedClaim === index ? P.panel2 : "transparent",
                }}
              >
                <span style={{ fontFamily: MONO, fontSize: 10.5, color: P.faint }}>{index + 1}</span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontFamily: SERIF, fontSize: 14, color: P.text, display: "block" }}>
                    {claim.title || claim.id}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: 10.5, color: P.faint, display: "block" }}>
                    {claim.sentence}
                    {claim.granted ? " · placed, not yet shown" : ""}
                  </span>
                </span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: P.dim }}>
                  {claim.instruments.length ? "shelf ›" : "—"}
                </span>
              </button>
            ))}
          </div>
        )}
        {planet.dockQuestion && (
          <div
            style={{
              fontFamily: SERIF, fontSize: 13.5, color: P.dim, lineHeight: 1.5,
              marginTop: 8, paddingLeft: 10, borderLeft: `2px solid ${P.line}`,
            }}
          >
            {planet.dockQuestion}
          </div>
        )}
        {planet.tracks.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
            {planet.tracks.map((id) => (
              <span
                key={id}
                style={{
                  fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.1em",
                  padding: "3px 9px", borderRadius: 999,
                  border: `1px solid ${trackColour(P, id, hash32)}`,
                  color: trackColour(P, id, hash32),
                }}
              >
                {trackLabel(id)}
              </span>
            ))}
          </div>
        )}
        {planet.prereqs.length > 0 && (
          <Neighbours
            label="best after" ids={planet.prereqs} colour={P.gold}
            name={name} onPlanet={onPlanet}
          />
        )}
        {planet.opens.length > 0 && (
          <Neighbours
            label="opens the way to" ids={planet.opens} colour={P.faint}
            name={name} onPlanet={onPlanet}
          />
        )}
      </div>
    </div>
  );
}

function Neighbours({
  label, ids, colour, name, onPlanet,
}: {
  label: string;
  ids: string[];
  colour: string;
  name: (id: string) => string;
  onPlanet?: (id: string) => void;
}) {
  return (
    <div style={{ fontFamily: MONO, fontSize: 11.5, color: colour, marginTop: 6 }}>
      {label} ·{" "}
      {ids.map((id, index) => (
        <span key={id}>
          {index > 0 ? " · " : ""}
          <button
            type="button"
            onClick={() => onPlanet?.(id)}
            style={{
              background: "none", border: "none", padding: 0, cursor: onPlanet ? "pointer" : "default",
              fontFamily: MONO, fontSize: 11.5, color: colour, textDecoration: "underline dotted",
            }}
          >
            {name(id)}
          </button>
        </span>
      ))}
    </div>
  );
}

function Ladder({ planet, P }: { planet: PlanetReading; P: Palette }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
      {planet.rungs.map((rung, index) => (
        <span
          key={rung.name}
          style={{
            fontFamily: MONO,
            fontSize: 11,
            padding: "3px 9px",
            borderRadius: 999,
            border: `1px solid ${rung.met ? P.lv[(index + 1) % P.lv.length] : P.line}`,
            color: rung.met ? P.lv[(index + 1) % P.lv.length] : rung.active ? P.dim : P.faint,
            opacity: rung.active || rung.met ? 1 : 0.55,
          }}
          title={rung.active ? undefined : "no live materials reach this rung yet"}
        >
          {rung.met ? "✓ " : ""}
          {rung.name.toLowerCase()}
          {!rung.active && !rung.met ? " · dormant" : ""}
        </span>
      ))}
    </div>
  );
}

export function ClaimShelf({
  claim, index, total, P, onOpen,
}: {
  claim: ClaimReading;
  index: number;
  total: number;
  P: Palette;
  onOpen: (instrument: InstrumentReading) => void;
}) {
  const levels: [1 | 2 | 3, InstrumentReading[]][] = [1, 2, 3].map((level) => [
    level as 1 | 2 | 3,
    claim.instruments.filter((item) => item.level === level),
  ]);
  const accent = P.lv[Math.min(P.lv.length - 1, claim.rungs.filter((r) => r.met).length)];
  return (
    <div style={CARD(P, accent)} data-testid="atlas-claim-shelf">
      <div style={{ padding: "10px 14px 4px" }}>
        <div style={KICKER(accent)}>
          {`CLAIM ${index + 1} OF ${total} · ${claim.state}${claim.granted ? " · ON CREDIT" : ""}`}
        </div>
        <div style={{ fontFamily: SERIF, fontSize: 18, color: P.text, lineHeight: 1.3, marginTop: 4 }}>
          {claim.title || claim.id}
        </div>
        {claim.text && (
          <div style={{ fontFamily: SERIF, fontSize: 14, color: P.dim, lineHeight: 1.5, marginTop: 4 }}>
            {claim.text}
          </div>
        )}
        <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 13, color: P.gold, marginTop: 6 }}>
          {claim.sentence}
        </div>
      </div>
      {claim.instruments.length === 0 && (
        <div style={{ padding: "6px 14px 12px", fontFamily: MONO, fontSize: 11, color: P.faint }}>
          nothing has been published for this idea yet
        </div>
      )}
      {levels.map(([level, items]) =>
        items.length === 0 ? null : (
          <div key={level} style={{ padding: "8px 14px 0" }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.12em", color: P.lv[level % P.lv.length] }}>
              {`L${level} · ${items.length} instrument${items.length === 1 ? "" : "s"}`}
            </div>
            {items.map((item) => (
              <button
                key={`${item.itemId}@${item.version}`}
                type="button"
                onClick={() => onOpen(item)}
                disabled={!item.ref}
                data-testid={`atlas-instrument-${item.itemId}`}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  borderRadius: 10,
                  padding: "9px 10px",
                  marginTop: 6,
                  minHeight: 44,
                  cursor: item.ref ? "pointer" : "not-allowed",
                  border: `1px solid ${item.seen ? `${P.lv[level % P.lv.length]}77` : P.line}`,
                  background: item.seen ? `${P.lv[level % P.lv.length]}${P.night ? "22" : "18"}` : "transparent",
                  opacity: item.ref ? 1 : 0.55,
                }}
              >
                <span style={{ fontFamily: MONO, fontSize: 14, lineHeight: "18px", color: item.seen ? P.lv[level % P.lv.length] : P.faint }}>
                  {GLYPH[item.type] ?? "◆"}
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontFamily: MONO, fontSize: 12.5, color: P.text, display: "block", lineHeight: 1.45 }}>
                    {item.label}
                  </span>
                  <span
                    style={{
                      fontFamily: MONO,
                      fontSize: 11,
                      display: "block",
                      color: item.state === "SHAKEN" ? P.russet : item.due ? P.gold : P.faint,
                    }}
                  >
                    {KIND_WORD[item.type] ?? item.type}
                    {item.primary ? "" : " · passenger"}
                    {item.seen ? ` · ${item.state.toLowerCase()}` : ""}
                    {item.title ? ` · in ${item.title}` : ""}
                  </span>
                </span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: item.ref ? P.dim : P.faint }}>
                  {item.ref ? (item.seen ? "again ›" : "open ›") : "unpublished"}
                </span>
              </button>
            ))}
          </div>
        ),
      )}
      <div style={{ padding: "10px 14px 12px", fontFamily: MONO, fontSize: 11, color: P.faint, lineHeight: 1.6 }}>
        ◆ stake · ■ rep · ▲ scenario · ★ capstone
      </div>
    </div>
  );
}

/** One world as a row of a roster — the ring's, or the family's. */
function WorldRow({
  planet, P, onPlanet, testid,
}: {
  planet: PlanetReading;
  P: Palette;
  onPlanet: (id: string) => void;
  testid: string;
}) {
  const accent = planet.state === "COLD" ? P.faint : seasonColour(P, planet.heat);
  return (
    <button
      type="button"
      onClick={() => onPlanet(planet.id)}
      data-testid={testid}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "12px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        minHeight: 56,
        background: "transparent",
        border: "none",
        borderBottom: `1px solid ${P.line}55`,
        cursor: "pointer",
      }}
    >
      <span
        style={{
          width: 13, height: 13, borderRadius: 999, display: "inline-block", flexShrink: 0,
          background: planet.state === "CROSSED" ? accent : "transparent",
          border:
            planet.state === "CROSSED"
              ? `2px solid ${accent}`
              : planet.state === "COLD"
                ? `1.5px solid ${P.dim}`
                : `1.5px dashed ${P.gold}`,
          boxShadow: planet.state === "CROSSED" ? `0 0 6px ${accent}88` : "none",
        }}
      />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ fontFamily: SERIF, fontSize: 15, color: P.text, display: "block" }}>
          {planet.sun && <span style={{ fontFamily: MONO, fontSize: 12, color: P.gold }}>☉ </span>}
          {planet.title || planet.id}
        </span>
        {planet.hook && (
          <span
            style={{
              fontFamily: SERIF, fontStyle: "italic", fontSize: 13,
              color: planet.state === "COLD" ? P.gold : P.dim, display: "block", lineHeight: 1.35,
            }}
          >
            {planet.hook}
          </span>
        )}
        <span style={{ fontFamily: MONO, fontSize: 11, color: P.faint, display: "block", marginTop: 2 }}>
          {STATE_WORD[planet.state]} · {planet.claimsTotal} claim{planet.claimsTotal === 1 ? "" : "s"}
          {planet.claimsBurned ? ` · ${planet.claimsBurned} to repair` : ""}
        </span>
      </span>
      <span style={{ fontFamily: MONO, fontSize: 12, color: P.dim }}>fly ›</span>
    </button>
  );
}

/** A ring's worlds, family by family — the wedges, read along one orbit. */
export function RingRoster({
  ring, planets, sectors, P, colour, onPlanet,
}: {
  ring: RingReading;
  planets: PlanetReading[];
  sectors: SectorReading[];
  P: Palette;
  colour: string;
  onPlanet: (id: string) => void;
}) {
  const sun = planets.filter((planet) => planet.sun);
  const groups = sectors
    .map((sector) => ({ sector, members: planets.filter((planet) => !planet.sun && planet.sector === sector.id) }))
    .filter((group) => group.members.length);
  const unfiled = planets.filter((planet) => !planet.sun && !sectors.some((sector) => sector.id === planet.sector));
  return (
    <div style={CARD(P, colour)} data-testid="atlas-ring-roster">
      <div
        style={{
          padding: "8px 14px",
          ...KICKER(colour),
          borderBottom: `1px solid ${P.line}`,
        }}
      >
        {`${(ring.name || ring.id).toUpperCase()} — ${ring.crossed} OF ${ring.total} CROSSED`}
      </div>
      {sun.map((planet) => (
        <WorldRow key={planet.id} planet={planet} P={P} onPlanet={onPlanet} testid={`atlas-roster-${planet.id}`} />
      ))}
      {groups.map(({ sector, members }) => (
        <div key={sector.id}>
          <div style={{ ...KICKER(colour), padding: "8px 14px 2px", opacity: 0.85 }}>
            {`${sector.symbol} ${sector.label.toUpperCase()}`}
          </div>
          {members.map((planet) => (
            <WorldRow key={planet.id} planet={planet} P={P} onPlanet={onPlanet} testid={`atlas-roster-${planet.id}`} />
          ))}
        </div>
      ))}
      {unfiled.map((planet) => (
        <WorldRow key={planet.id} planet={planet} P={P} onPlanet={onPlanet} testid={`atlas-roster-${planet.id}`} />
      ))}
    </div>
  );
}

/** A family's worlds, ring by ring — one column, read from the centre out. */
export function SectorCard({
  sector, rings, planets, colour, P, onPlanet, onClose,
}: {
  sector: SectorReading;
  rings: RingReading[];
  planets: PlanetReading[];
  colour: string;
  P: Palette;
  onPlanet: (id: string) => void;
  onClose: () => void;
}) {
  const crossed = planets.filter((planet) => planet.state === "CROSSED").length;
  return (
    <div style={CARD(P, colour)} data-testid="atlas-sector-card">
      <div
        style={{
          padding: "8px 14px", display: "flex", alignItems: "baseline", gap: 10,
          borderBottom: `1px solid ${P.line}`,
        }}
      >
        <span style={KICKER(colour)}>{`${sector.symbol} ${sector.label.toUpperCase()}`}</span>
        <span style={{ fontFamily: MONO, fontSize: 11, color: P.faint }}>
          {crossed} of {planets.length} crossed
        </span>
        <button
          type="button" onClick={onClose}
          style={{
            marginLeft: "auto", background: "none", border: "none", cursor: "pointer",
            fontFamily: MONO, fontSize: 12, color: P.dim, minHeight: 36, padding: "0 4px",
          }}
        >
          the whole sky ›
        </button>
      </div>
      {rings.map((ring, index) => {
        const here = ring.planets
          .map((id) => planets.find((planet) => planet.id === id))
          .filter((planet): planet is PlanetReading => !!planet);
        if (!here.length) return null;
        return (
          <div key={ring.id}>
            <div style={{ ...KICKER(P.rings[index % P.rings.length]), padding: "8px 14px 2px" }}>
              {(ring.name || ring.id).toUpperCase()}
            </div>
            {here.map((planet) => (
              <WorldRow key={planet.id} planet={planet} P={P} onPlanet={onPlanet} testid={`atlas-column-${planet.id}`} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function SkyCard({
  data, P, onPlanet,
}: {
  data: AtlasPayload;
  P: Palette;
  onPlanet: (id: string) => void;
}) {
  const first =
    data.planets.find((planet) => planet.state !== "COLD" && planet.nextClaim) ??
    data.planets.find((planet) => planet.prereqs.length === 0 && planet.ringLevel !== null) ??
    data.planets[0];
  return (
    <div style={CARD(P, P.gold)} data-testid="atlas-sky-card">
      <div style={{ padding: "10px 14px 12px" }}>
        <div style={KICKER(P.gold)}>THE RINGS · HOW DEEP — AND, ACROSS THEM, WHICH FAMILY</div>
        {data.rings.map((ring, index) => (
          <div key={ring.id} style={{ marginTop: 8 }}>
            <div style={{ fontFamily: MONO, fontSize: 12.5, color: P.rings[index % P.rings.length] }}>
              {ring.name || ring.id}
              <span style={{ color: P.dim }}>
                {" "}· {ring.crossed} of {ring.total} crossed
              </span>
            </div>
            <div
              style={{
                height: 3, borderRadius: 999, marginTop: 4,
                background: P.panel2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.round(ring.climb * 100)}%`, height: "100%",
                  background: P.rings[index % P.rings.length], opacity: 0.75,
                }}
              />
            </div>
          </div>
        ))}
        {data.sectors.length > 0 && (
          <div style={{ fontFamily: MONO, fontSize: 11.5, color: P.dim, marginTop: 10, lineHeight: 1.6 }}>
            THE FAMILIES · WHICH WEDGE —{" "}
            {data.sectors.map((sector) => `${sector.symbol} ${sector.label}`).join(" · ")}
          </div>
        )}
        <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, color: P.dim, marginTop: 12, lineHeight: 1.5 }}>
          every world holds a handful of claims · each claim carries a shelf of instruments ·
          answer them and the world warms, fills, and finally crosses
        </div>
        {first && (
          <button
            type="button"
            onClick={() => onPlanet(first.id)}
            data-testid="atlas-begin"
            style={{
              marginTop: 12, borderRadius: 999, padding: "9px 16px", minHeight: 44,
              fontFamily: MONO, fontSize: 12.5, border: `1px solid ${P.gold}`,
              color: P.gold, background: "transparent", cursor: "pointer",
            }}
          >
            begin the thread → {first.title || first.id}
          </button>
        )}
      </div>
    </div>
  );
}

export function TrackCard({
  track, planets, P, onPlanet,
}: {
  track: TrackReading;
  planets: PlanetReading[];
  P: Palette;
  onPlanet: (id: string) => void;
}) {
  const colour = trackColour(P, track.id, hash32);
  const crossed = planets.filter((planet) => planet.state === "CROSSED").length;
  return (
    <div style={CARD(P, colour)} data-testid="atlas-track-card">
      <div style={{ padding: "10px 14px 4px" }}>
        <div style={KICKER(colour)}>
          {`THE ${track.label} ROUTE · ${crossed} OF ${planets.length} CROSSED`}
        </div>
        <div style={{ fontFamily: SERIF, fontSize: 18, color: P.text, lineHeight: 1.3, marginTop: 4 }}>
          {track.tagline}
        </div>
        <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 13.5, color: P.dim, lineHeight: 1.55, marginTop: 6 }}>
          {track.blurb}
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 14px 4px" }}>
        {planets.map((planet, index) => (
          <button
            key={planet.id}
            type="button"
            onClick={() => onPlanet(planet.id)}
            data-testid={`atlas-route-${planet.id}`}
            style={{
              borderRadius: 999, padding: "5px 10px", minHeight: 34, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              border: `1px solid ${planet.state === "COLD" ? P.line : colour}`,
              color: planet.state === "COLD" ? P.dim : colour,
              background: planet.state === "CROSSED" ? `${colour}${P.night ? "33" : "22"}` : "transparent",
              fontFamily: MONO, fontSize: 11,
            }}
          >
            <span style={{ opacity: 0.7, fontSize: 9.5 }}>{index + 1}</span>
            {planet.title || planet.id}
            {planet.state !== "COLD" && (
              <span
                style={{
                  width: 6, height: 6, borderRadius: 999, display: "inline-block",
                  background: seasonColour(P, planet.heat),
                }}
              />
            )}
          </button>
        ))}
      </div>
      <div style={{ padding: "6px 14px 12px", fontFamily: MONO, fontSize: 11, color: P.faint, lineHeight: 1.6 }}>
        walking order, not law — the sky never changes; a route only lights one way through it,
        and every world beyond it stays one tap away
      </div>
    </div>
  );
}
